import { useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Option } from 'types';

import {
  CreateSupportTicketPayload,
  ScreenshotItem,
  supportTicketDataService,
} from 'services/supportTicket.service';

const DEFAULT_PRIORITY: CreateSupportTicketPayload['priority'] = 'Normal';

// Matches wwg-experience's create-colmar-ticket content-type allow-list
// exactly — kept in sync by hand since the two apps don't share a package;
// validating client-side too avoids sending an obviously-bad file at all.
const ALLOWED_SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

// Decision 16: up to 5 screenshots, 5MB combined (not per file) — colmar-
// be-nestjs's DTO and wwg-experience's Edge Function both enforce the same
// two numbers now, so this is the client-side mirror of both.
const MAX_SCREENSHOTS = 5;

// Decision 15/16: base64 inflates the payload ~33%, and wwg-experience's
// create-colmar-ticket Edge Function was empirically confirmed to hit a
// 546 WORKER_RESOURCE_LIMIT somewhere around a 10MB request body. 5MB
// combined leaves real headroom below that boundary — it's a resource
// limit, not a fixed byte cap, so it can plausibly shift under load.
const MAX_SCREENSHOTS_COMBINED_BYTES = 5 * 1024 * 1024;

// Decision 14: the screenshot travels inline, base64-encoded, in the same
// POST as the rest of the ticket — no separate upload endpoint anymore.
const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      // reader.result is "data:image/png;base64,AAAA..." — only the part
      // after the comma is the actual base64 payload the backend expects.
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);

    reader.readAsDataURL(file);
  });

const useSupportTicketWidget = () => {
  const { t } = useTranslation();

  const [isOpen, isOpenSet] = useState(false);
  const [isSubmitting, isSubmittingSet] = useState(false);
  // Holds only the raw value ('Low'/'Normal'/...), never a translated label —
  // storing `{ value, label }` (as returned by Select's onChange) froze the
  // label text at whatever language was active when it was last set, so
  // switching the app language afterwards left the selected option's label
  // stuck in the old language even though the dropdown's own options (below)
  // re-translated correctly. Deriving the displayed option fresh every
  // render from `priorityOptions` keeps it reactive to language changes too.
  const [priorityValue, priorityValueSet] =
    useState<CreateSupportTicketPayload['priority']>(DEFAULT_PRIORITY);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onSubmit' });

  const priorityOptions: Option[] = [
    { value: 'Low', label: t('supportTicket.priorityLow') },
    { value: 'Normal', label: t('supportTicket.priorityNormal') },
    { value: 'High', label: t('supportTicket.priorityHigh') },
    { value: 'Critical', label: t('supportTicket.priorityCritical') },
  ];

  const priority =
    priorityOptions.find((option) => option.value === priorityValue) ?? priorityOptions[1];

  // TextArea (components/TextArea) doesn't expose an external ref, only
  // react-hook-form's own — so the element is captured here, from the one
  // DOM event TextArea does forward, rather than by adding ref support to
  // that shared component just for this.
  const descriptionElRef = useRef<HTMLTextAreaElement | null>(null);
  const [descriptionLength, descriptionLengthSet] = useState(0);

  const autoGrowDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const el = e.target;

    descriptionElRef.current = el;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    descriptionLengthSet(el.value.length);
  };

  const onPriorityChange = (option: Option) =>
    priorityValueSet(option.value as CreateSupportTicketPayload['priority']);

  // Screenshots are deliberately NOT a react-hook-form field — they need an
  // async base64-read step before they can join the submit payload (see
  // readFileAsBase64/onSubmit below), which doesn't fit react-hook-form's
  // synchronous field model — so it's plain component state instead.
  // Decision 16: an array (up to MAX_SCREENSHOTS), replacing the single
  // File | null from Decision 14 — one preview URL per file, same order.
  const [screenshotFiles, screenshotFilesSet] = useState<File[]>([]);
  const [screenshotPreviewUrls, screenshotPreviewUrlsSet] = useState<string[]>([]);
  const screenshotInputRef = useRef<HTMLInputElement | null>(null);

  const removeScreenshot = (index: number) => {
    URL.revokeObjectURL(screenshotPreviewUrls[index]);
    screenshotFilesSet((files) => files.filter((_file, i) => i !== index));
    screenshotPreviewUrlsSet((urls) => urls.filter((_url, i) => i !== index));

    // Clears the native input's own value so selecting the exact same file
    // again still fires onChange (browsers don't fire "change" for a
    // no-op re-selection of the currently-set value).
    if (screenshotInputRef.current) {
      screenshotInputRef.current.value = '';
    }
  };

  const removeAllScreenshots = () => {
    screenshotPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    screenshotFilesSet([]);
    screenshotPreviewUrlsSet([]);

    if (screenshotInputRef.current) {
      screenshotInputRef.current.value = '';
    }
  };

  // UX decisions for "too many files" / "too many bytes" (not pre-specified
  // by the task, decided and documented here):
  // - Excess files beyond the 5-file cap are dropped silently from
  //   processing (never even type/size-checked) and reported as one toast
  //   naming how many were skipped — already-selected files are untouched.
  // - Each candidate is then checked individually (type, then running
  //   combined size against the 5MB cap) in the order picked; a file that
  //   would push the combined total over the cap is skipped on its own —
  //   files before it in the same selection that already fit are still
  //   kept, rather than rejecting the whole batch.
  // - Every distinct rejection reason fires at most one toast per
  //   selection, not one per file, to avoid a wall of toasts from a single
  //   multi-file pick.
  const handleScreenshotSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pickedFiles = Array.from(e.target.files ?? []);

    if (pickedFiles.length === 0) return;

    const availableSlots = MAX_SCREENSHOTS - screenshotFiles.length;
    const overflowCount = Math.max(0, pickedFiles.length - availableSlots);
    const candidates = pickedFiles.slice(0, Math.max(0, availableSlots));

    let combinedBytes = screenshotFiles.reduce((sum, file) => sum + file.size, 0);
    const acceptedFiles: File[] = [];
    let hasTypeRejection = false;
    let hasSizeRejection = false;

    candidates.forEach((file) => {
      if (!ALLOWED_SCREENSHOT_TYPES.includes(file.type)) {
        hasTypeRejection = true;
        return;
      }

      if (combinedBytes + file.size > MAX_SCREENSHOTS_COMBINED_BYTES) {
        hasSizeRejection = true;
        return;
      }

      combinedBytes += file.size;
      acceptedFiles.push(file);
    });

    if (overflowCount > 0) {
      toast.error(t('supportTicket.screenshotCountError', { count: MAX_SCREENSHOTS }));
    }
    if (hasTypeRejection) {
      toast.error(t('supportTicket.screenshotTypeError'));
    }
    if (hasSizeRejection) {
      toast.error(t('supportTicket.screenshotSizeError'));
    }

    if (acceptedFiles.length > 0) {
      screenshotFilesSet((files) => [...files, ...acceptedFiles]);
      screenshotPreviewUrlsSet((urls) => [
        ...urls,
        ...acceptedFiles.map((file) => URL.createObjectURL(file)),
      ]);
    }

    // Always clear the input value, accepted or not — otherwise picking the
    // exact same (rejected) file again wouldn't re-fire onChange.
    if (screenshotInputRef.current) {
      screenshotInputRef.current.value = '';
    }
  };

  const open = () => isOpenSet(true);

  const close = () => {
    if (isSubmitting) return;

    isOpenSet(false);
    reset();
    priorityValueSet(DEFAULT_PRIORITY);
    removeAllScreenshots();
    descriptionLengthSet(0);

    if (descriptionElRef.current) {
      descriptionElRef.current.style.height = '';
    }
  };

  const onSubmit = handleSubmit(async (data: FieldValues) => {
    isSubmittingSet(true);

    try {
      // Reading every file is now just a local, synchronous-feeling step
      // ahead of the one and only network call — if it (or the submit
      // itself) fails, the single catch block below covers both, same as
      // a screenshot-less submission.
      const screenshots: ScreenshotItem[] | undefined =
        screenshotFiles.length > 0
          ? await Promise.all(
              screenshotFiles.map(async (file) => ({
                screenshot: await readFileAsBase64(file),
                screenshotContentType: file.type,
              }))
            )
          : undefined;

      await supportTicketDataService.submitTicket({
        subject: data.subject,
        description: data.description,
        priority: priorityValue,
        screenshots,
      });

      toast.success(t('supportTicket.submittedSuccessfully'));
      close();
    } catch {
      // Decision 1 (Feature Context): a failed submission must be surfaced
      // to the user, never treated as a silent success.
      toast.error(t('supportTicket.submitFailed'));
    } finally {
      isSubmittingSet(false);
    }
  });

  return {
    isOpen,
    open,
    close,
    register,
    errors,
    onSubmit,
    isSubmitting,
    priority,
    onPriorityChange,
    priorityOptions,
    autoGrowDescription,
    descriptionLength,
    screenshotFiles,
    screenshotPreviewUrls,
    screenshotInputRef,
    handleScreenshotSelect,
    removeScreenshot,
    canAddMoreScreenshots: screenshotFiles.length < MAX_SCREENSHOTS,
  };
};

export { useSupportTicketWidget };
